import React, { useState } from "react";
import {
  X,
  MapPin,
  Calendar,
  Download,
  Share2,
  Link as LinkIcon,
  ChevronRight,
  User,
  Info,
  FileText,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Event, EventFormat } from "../../types/event.d";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface EventDetailViewProps {
  event: Event;
  onClose: () => void;
  onRegister?: (event: Event) => void;
  onAddCalendar?: (event: Event) => void;
}

const EventDetailView: React.FC<EventDetailViewProps> = ({
  event,
  onClose,
  onAddCalendar,
}) => {
  const [question, setQuestion] = useState("");

  const eventDate = new Date(event.date);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-slate-100">
        <div className="flex items-center gap-4">
          <div className="bg-rajapiBlue p-3 rounded-xl text-white">
            <Calendar size={32} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{event.title}</h2>
            <p className="text-slate-500 uppercase text-xs font-semibold tracking-wider">
              {event.category}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-400"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Info Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3">
                <div className="p-2 bg-blue-50 text-rajapiBlue rounded-lg">
                  <Info size={18} />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-slate-400">
                    Thématique
                  </p>
                  <p className="text-sm font-bold text-slate-700">
                    {event.category}
                  </p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3">
                <div className="p-2 bg-blue-50 text-rajapiBlue rounded-lg">
                  <MapPin size={18} />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-slate-400">
                    Localisation
                  </p>
                  <p className="text-sm font-bold text-slate-700">
                    {event.location}
                  </p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3">
                <div className="p-2 bg-blue-50 text-rajapiBlue rounded-lg">
                  <Calendar size={18} />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-slate-400">
                    Durée / Date
                  </p>
                  <p className="text-sm font-bold text-slate-700">
                    {format(eventDate, "dd MMM yyyy", { locale: fr })}
                  </p>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm relative">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-900">
                  Informations sur l'évènement
                </h3>
                <button className="text-slate-300 hover:text-slate-500">
                  <ChevronRight size={18} />
                </button>
              </div>
              <p className="text-slate-600 leading-relaxed italic">
                "{event.description}"
              </p>
            </div>

            {/* Intervenants Section */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900">
                  Membres du projet / Intervenants
                </h3>
                <button className="text-slate-300 hover:text-slate-500">
                  <ChevronRight size={18} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {event.speakers?.map((speaker) => (
                  <div key={speaker.id} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden">
                      {speaker.avatar ? (
                        <img
                          src={speaker.avatar}
                          alt={speaker.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                          <User size={20} />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">
                        {speaker.name}
                      </p>
                      <p className="text-xs text-slate-500">{speaker.role}</p>
                    </div>
                  </div>
                ))}
                {!event.speakers?.length && (
                  <p className="text-sm text-slate-400">
                    Aucun intervenant listé.
                  </p>
                )}
              </div>
            </div>

            {/* Questions Section */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-2">
                Questions avant l'évènement
              </h3>
              <p className="text-xs text-slate-500 mb-4 italic">
                Vous avez une question pour nos intervenants ? Posez-la ici.
              </p>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Saisissez votre question ici..."
                className="w-full h-24 p-4 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-rajapiBlue/30 text-slate-600 resize-none mb-4"
              />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  className="bg-slate-50 border-slate-100 text-slate-600 text-sm h-10"
                >
                  Envoyer ma question
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar Area */}
          <div className="space-y-6">
            {/* Enrollment Sidebar Card */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  Enrollment
                </p>
                <Badge
                  variant="info"
                  className="bg-blue-100 text-blue-600 border-none font-bold text-[10px]"
                >
                  {event.format}
                </Badge>
              </div>
              <div className="mb-6">
                <p className="text-3xl font-bold text-slate-900">
                  ${event.price || "0.00"}{" "}
                  <span className="text-sm text-slate-400 font-normal">
                    / person
                  </span>
                </p>
              </div>
              <div className="space-y-3">
                <Button className="w-full bg-emerald-800 hover:bg-emerald-900 text-white h-12 flex items-center justify-center gap-2 rounded-xl">
                  <User size={18} />
                  <span>S'inscrire Maintenant</span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 border-slate-100 text-slate-600 flex items-center justify-center gap-2 rounded-xl"
                  onClick={() => onAddCalendar?.(event)}
                >
                  <Calendar size={18} />
                  <span>Add to Calendar</span>
                </Button>
              </div>
            </div>

            {/* Documents Sidebar Card */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <p className="text-[10px] uppercase font-bold text-slate-400 mb-4">
                Documents
              </p>
              <div className="space-y-3">
                {event.documents?.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl group hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                        <FileText size={18} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 truncate max-w-[120px]">
                          {doc.name}
                        </p>
                        <p className="text-[10px] text-slate-400 uppercase">
                          {doc.size || "1.0 MB"}
                        </p>
                      </div>
                    </div>
                    <button className="text-slate-300 group-hover:text-slate-500">
                      <Download size={16} />
                    </button>
                  </div>
                ))}
                {!event.documents?.length && (
                  <p className="text-xs text-slate-400 italic">
                    Aucun document disponible.
                  </p>
                )}
              </div>
            </div>

            {/* Share Sidebar Card */}
            {event.format === EventFormat.ONLINE && (
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-slate-400 mb-4">
                  Share Network
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 bg-slate-50 border-none h-10 flex items-center justify-center rounded-xl text-slate-400"
                  >
                    <Share2 size={18} />
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 bg-slate-50 border-none h-10 flex items-center justify-center rounded-xl text-slate-400"
                  >
                    <LinkIcon size={18} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer info as seen in design */}
      <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center text-[10px] uppercase font-bold text-slate-300 tracking-widest">
        <div>Host ID: RC-9921 • Format: Live Webinar</div>
        <div>Ref: #EVT-2024-AI-CLM</div>
      </div>
    </div>
  );
};

export default EventDetailView;
